import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import _ from 'lodash';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import MiscUtils from 'services/MiscUtils';
import PreviewDocumentModal from 'components/Modals/PreviewDocumentModal';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import Icon from 'components/Utils/NewIcon';
import UploadFile from 'components/Utils/UploadFile';
import DefaultModal from 'components/Utils/DefaultModal';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DocumentPreview from 'components/Utils/DocumentPreview';
import DocumentUtils from 'services/DocumentUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import MenuItem from '@material-ui/core/MenuItem';
import OtoscopeScreen from 'components/Misc/OtoscopeScreen';
import { RemindersButton } from 'components/Patient/Reminders';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import DefaultTextField from 'components/Utils/DefaultTextField';
import { TextareaAutosize, IconButton, Menu, ListItemIcon } from '@material-ui/core';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import UnreadBadge from 'components/Common/UnreadBadge';
import CalendarUtils from 'services/CalendarUtils';
import ListItemText from '@material-ui/core/ListItemText';
import { Flows } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/Flow/flows';
import { applyReasonToInsertedStamp } from '../stampUtils';
import { APPOINTMENT_STATUS } from 'services/AppointmentUtils';
import { IsTypeNurse } from 'careand-redux/selectors';
import AIUtils from 'services/AIUtils';
import useAssistant from 'hooks/useAssistant';
import { DocumentCheck } from 'components/HealthRecords/Documents/DocumentCheck';
import DocumentBadge from 'components/HealthRecords/Documents/DocumentBadge';
import List from '@material-ui/core/List';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { GetPhysician } from 'careand-redux/selectors';
import useDocumentSelection from 'hooks/useDocumentSelection';

export const MODES = {
  addAttachment: 'addAttachment',
  addStamp: 'addStamp',
  previewAttachment: 'previewAttachment',
  otoscope: 'otoscope',
};

export async function uploadFileToCPP(
  file,
  { setUploading, dispatch, isIntern, patientId, onSuccess },
) {
  if (file) {
    setUploading(true);

    let title = file.name;
    let approved = !isIntern;
    let type = DocumentUtils.DOCUMENT_TYPE.other;

    let result = await dispatch(
      api.Documents.addDocument({
        file,
        title,
        approved,
        documentType: type,
        id: patientId,
      }),
      {
        errorTitle: 'File Upload',
      },
    );

    if (result) {
      onSuccess(result);
    }
    setUploading(false);
  }
}

export const AttachmentSidePanel = (props) => {
  const dispatch = useAppDispatch();
  const defaultStampId = useAppSelector(
    (state) => GetPhysician(state)?.preferences?.default_soap_stamp,
  );

  const {
    noteId,
    patient,
    onChange,
    form,
    valueRef,
    documents,
    currentMode,
    setCurrentMode,
    showBeautify,
    reason,
    draftId,
    onClose,
    appointmentId,
    insertText,
  } = props;

  const authentication = useAppSelector((state) => state.authentication);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const templatesSelector = useAppSelector((state) => state.templates);

  const [template, setTemplate] = useState();
  const [categoryTemplates, setCategoryTemplates] = useState();
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [attachmentsCache, setAttachmentsCache] = useState(null);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [currentStampId, setCurrentStampId] = useState(form.stamp);
  const [stamps, setStamps] = useState({});
  const [edittingStamp, setEdittingStamp] = useState();
  const [creatingStamp, setCreatingStamp] = useState();

  const templates = templatesSelector.templates;
  const ownId = authentication.user.id;
  const ownPhysician = physicians[ownId];
  const stampRefs = useAppSelector((state) => state.healthRecords.appointmentNoteStamps);
  const isNurse = useAppSelector(IsTypeNurse);
  const isLoadingStamp = useIsLoading(api.HealthRecords.getAppointmentNoteStamp);

  const filteredStamps = useMemo(() => {
    if (isNurse) {
      return stampRefs.filter((stamp) => stamp.name?.includes('RPN'));
    }
    return stampRefs;
  }, [stampRefs, isNurse]);

  async function getStamp(id) {
    id = id ?? currentStampId;
    let stamp = stamps[id];
    if (!stamp) {
      stamp = await dispatch(api.HealthRecords.getAppointmentNoteStamp({ id }));
      setStamps((prev) => ({ ...prev, [id]: stamp }));
    }
    return stamp;
  }

  async function onApplyStamp() {
    let stamp = await getStamp(currentStampId);

    if (stamp) {
      insertText(applyReasonToInsertedStamp(stamp.content, reason, valueRef?.current));
    }

    setCurrentMode(null);
  }

  const isIntern = useMemo(() => {
    let user = ownPhysician || authentication.user;
    return !!user.intern_of;
  }, [authentication, ownPhysician]);

  function onClickedFlow(flow) {
    dispatch(
      uiActions.addPinnedDocument({
        patientId: patient.id,
        type: 'flow',
        id: form,
        payload: { reason, flow, draftId, itemId: noteId },
      }),
    );
    onClose();
  }

  const templateCategories = useMemo(() => {
    return PerformanceUtils.sortBy(templatesSelector.templateCategories, ['name'], false);
  }, [templatesSelector.templateCategories]);

  const onFileUploaded = (file) =>
    uploadFileToCPP(file, {
      dispatch,
      setUploading: setUploadingAttachment,
      onSuccess: (document) => addDocumentToNote(document, file),
      patientId: patient.id,
      isIntern,
    });

  function addDocumentToNote(document, file) {
    let previews = { ...(attachmentsCache || {}) };
    if (file && file.type !== 'application/pdf') {
      previews[document.id] = file.preview;
      setAttachmentsCache(previews);
    }

    let newDocuments = [...(documents || [])];
    newDocuments.push(document.id);

    onChange('documents', newDocuments);
    setCurrentMode(null);

    setUploadingAttachment(false);
  }

  function chooseCategory(event) {
    if (!event) return;
    let category = event.value;
    onChange('template', null);
    if (category) {
      let chosenTemplates = (templates || [])
        .filter((template) => template.category_id === category.id)
        .map((template) => {
          return {
            value: template,
            label: template.description,
          };
        });
      chosenTemplates = PerformanceUtils.sortBy(chosenTemplates, ['label'], false);
      setCategoryTemplates(chosenTemplates);
    } else {
      setCategoryTemplates();
    }
  }

  async function deleteAttachment(attachment) {
    setDeletingAttachment(true);
    const { isSuccess } = await dispatch(api.Documents.deleteDocument(attachment.document.id), {
      errorTitle: 'Delete Document',
      meta: true,
    });
    if (isSuccess) {
      let newDocuments = [...(documents || [])];
      newDocuments = newDocuments.filter((document) => document !== attachment.document.id);
      onChange('documents', newDocuments);
      setCurrentMode(null);
      setCurrentAttachment(null);
      setShowConfirmationDialog(false);
    }
    setDeletingAttachment(false);
  }

  async function addTemplate(html, metadata, close) {
    let { title, document_type, hide = false } = metadata;

    let documentTemplateSubmission = {
      title: title || template.description,
      html,
      template_id: template.id,
      template_metadata: metadata ? JSON.stringify(metadata) : metadata,
      approved: !isIntern,
      document_type,
      hide,
    };

    let result = await dispatch(
      api.Documents.addDocumentFromHtml({
        documentTemplateSubmission,
        patientId: patient.id,
      }),
      { errorTitle: 'Add Document' },
    );

    if (result) {
      addDocumentToNote(result);
      if (close) setTemplate(false);
      return result;
    }
  }

  const closeOtoscope = useCallback(() => setCurrentMode(), []);

  return (
    <>
      {!!currentMode && (
        <>
          {currentMode === MODES.otoscope && (
            <DefaultModal
              key="otoscope-modal"
              className="modal large-modal"
              title="Otoscope"
              wrap={true}
              padding={false}
              content={<OtoscopeScreen patient={patient} onClose={closeOtoscope} />}
              isOpen={currentMode === MODES.otoscope}
            />
          )}
          {(!!edittingStamp || creatingStamp) && (
            <EditStampModal
              stamp={edittingStamp}
              create={creatingStamp}
              onStampUpdated={(updated) => {
                setStamps((prev) => ({ ...prev, [updated.id]: updated }));
              }}
              onRequestClose={() => {
                setEdittingStamp(undefined);
                setCreatingStamp(false);
              }}
            />
          )}
          <div className="attachment-preview">
            <Icon
              src="xmark"
              className="close-icon"
              alt="Close attachment"
              showTooltip={true}
              onClick={() => setCurrentMode(null)}
            />
            {currentMode === MODES.addStamp && (
              <Icon
                src="circle-plus"
                className="new-stamp-icon"
                alt="Create New Stamp"
                showTooltip={true}
                width="20px"
                onClick={() => setCreatingStamp(true)}
              />
            )}
            <Icon
              src="xmark"
              className="close-icon"
              alt="Close attachment"
              showTooltip={true}
              onClick={() => setCurrentMode(null)}
            />
            {currentMode === MODES.addAttachment && !template && (
              <div className="adding-attachment">
                <span className="small-title">Choose Template</span>

                <DefaultSelectField
                  name="template_category"
                  label=""
                  placeholder="Template Category"
                  value=""
                  onChange={(event) => {
                    const category = templateCategories.find((c) => c.id === event.target.value);
                    chooseCategory(category ? { value: category } : null);
                  }}
                  inputElements={(templateCategories || []).map((category) => (
                    <MenuItem value={category.id} key={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                  style={{ width: '100%', marginTop: '0' }}
                />

                {!!categoryTemplates && (
                  <DefaultSelectField
                    name="template"
                    label=""
                    placeholder="Template"
                    value=""
                    onChange={(event) => {
                      const tmpl = categoryTemplates.find(
                        (t) => t.value?.id === event.target.value,
                      );
                      if (tmpl?.value) setTemplate(tmpl.value);
                    }}
                    inputElements={categoryTemplates.map((t) => (
                      <MenuItem value={t.value?.id} key={t.value?.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                    style={{ width: '100%', marginTop: '0' }}
                  />
                )}

                <span className="or">OR</span>

                {!uploadingAttachment && (
                  <UploadFile
                    title="Upload File"
                    onDrop={onFileUploaded}
                    accept="image/jpeg, image/png, application/pdf"
                  />
                )}
                <ProgressMessage show={uploadingAttachment} size={50} />
              </div>
            )}

            {currentMode === MODES.addStamp && !template && (
              <div className="adding-stamp">
                <span className="small-title">Choose Stamp</span>

                <div className="flex w-full items-baseline">
                  <DefaultSelectField
                    name="stamp"
                    label="Stamps"
                    className="stamp-select flex-grow"
                    inputElements={filteredStamps.map(({ id, name }) => {
                      const isDefault = !!defaultStampId && id === defaultStampId;
                      return (
                        <MenuItem value={id} key={id}>
                          <span>
                            {name}
                            {isDefault ? (
                              <Icon
                                width="1rem"
                                height="1rem"
                                style={{ display: 'inline-block', marginLeft: '1ch' }}
                                src={['fas', 'star']}
                              />
                            ) : null}
                          </span>
                        </MenuItem>
                      );
                    })}
                    onChange={(event) => {
                      setCurrentStampId(event.target.value);
                      onChange('stamp', event.target.value);
                    }}
                    value={currentStampId}
                    style={{ width: '100%', marginTop: '0' }}
                  />

                  <IconButton
                    className="edit-stamp-button"
                    onClick={() => {
                      getStamp(currentStampId).then(setEdittingStamp);
                    }}
                    disabled={!currentStampId}
                  >
                    <Icon
                      loading={isLoadingStamp}
                      src="pencil"
                      height="20px"
                      alt="Edit"
                      showTooltip={true}
                    />
                  </IconButton>
                </div>
                <DefaultButton
                  name="Apply Stamp"
                  className="blue !mt-[1rem] block"
                  loading={isLoadingStamp}
                  onClick={onApplyStamp}
                />
                <span style={{ display: 'block', marginTop: '15px' }}>
                  The stamp will be pasted in the current cursor position.
                </span>
              </div>
            )}

            {currentMode === MODES.previewAttachment && !template && (
              <>
                <div className="preview">
                  <Icon
                    src="pencil"
                    className="edit-icon"
                    alt="Edit attachment"
                    showTooltip={true}
                    height="30px"
                    width="30px"
                    onClick={() => setOpenDetailsModal(true)}
                  />
                  <Icon
                    src="circle-trash"
                    className="delete-icon"
                    alt="Delete attachment"
                    showTooltip={true}
                    height="30px"
                    width="30px"
                    onClick={() => setShowConfirmationDialog(true)}
                  />
                  {showConfirmationDialog && (
                    <ConfirmationDialog
                      title="Are you sure you want to delete the document?"
                      message="Please click 'Confirm' to proceed with the removal."
                      open={showConfirmationDialog}
                      onConfirm={() => deleteAttachment(currentAttachment)}
                      onCancel={() => setShowConfirmationDialog(false)}
                      loadingState={deletingAttachment}
                    />
                  )}
                  <DocumentPreview
                    url={currentAttachment?.url}
                    type={currentAttachment?.type}
                    key={currentAttachment?.url}
                  />
                </div>
                {openDetailsModal && (
                  <DocumentDetailsModal
                    document={currentAttachment?.document}
                    isOpen={openDetailsModal}
                    onClose={() => setOpenDetailsModal(false)}
                    disableEdit={isIntern}
                    disablePatientBadge={true}
                    onEdit={() => {}}
                    allowExpand={true}
                    autoLoad={true}
                  />
                )}
              </>
            )}

            {!!template && (
              <DefaultModal
                className="large-modal"
                key="document-template-modal"
                content={
                  <DocumentTemplate
                    key={template?.id}
                    templateId={template?.id}
                    onSave={addTemplate}
                    onClose={() => setTemplate()}
                    patient={patient}
                    hidePrint={true}
                    noteForm={form}
                  />
                }
                isOpen={!!template}
                onRequestClose={() => setTemplate()}
              />
            )}
          </div>
        </>
      )}
      <AttachmentThumbnailsPanel
        noteId={noteId}
        note={valueRef.current}
        attachmentsCache={attachmentsCache}
        setCurrentAttachment={setCurrentAttachment}
        setCurrentMode={setCurrentMode}
        patient={patient}
        showBeautify={showBeautify}
        setFlow={onClickedFlow}
        insertText={insertText}
        appointmentId={appointmentId}
      />
    </>
  );
};

const DEFAULT_ARRAY = [];

const AttachmentThumbnailsPanel = ({
  noteId,
  note,
  documents = DEFAULT_ARRAY,
  setCurrentMode,
  attachmentsCache,
  setCurrentAttachment,
  patient,
  showBeautify,
  setFlow,
  insertText,
}) => {
  const allDocuments = useAppSelector((state) => state.healthRecords.byId[patient.id]?.documents);
  const otherNotes = useAppSelector((state) => state.healthRecords.byId[patient.id]?.notes);

  const [flowMenuAnchorEl, setFlowMenuAnchorEl] = useState(null);
  const [showPSuggestion, setShowPSuggestion] = useState(false);

  const documentsWithNotes = useMemo(() => {
    const mostRecentNote = _.reverse(
      _.sortBy(
        (otherNotes || []).filter((note) => note.id !== noteId).map((note) => note.value),
        'date',
      ),
    )[0];
    return (allDocuments || []).filter(
      (document) => document.comments && document.date >= mostRecentNote?.date,
    );
  }, [otherNotes, allDocuments, noteId]);

  const sortedDocuments = useMemo(() => {
    return PerformanceUtils.sortBy(documents, ['id']);
  }, [documents]);

  function onInsertDocumentNotes(documents) {
    let text = documents
      .map((document) => {
        return `${document.title} (${CalendarUtils.format(document.date)}) - ${document.comments}`;
      })
      .join('\n\n');
    insertText(text);
  }

  return (
    <div className="thumbnails">
      <Icon
        src="paperclip"
        className="add-icon blue"
        alt="Add attachment"
        showTooltip={true}
        height="25px"
        width="25px"
        onClick={() => {
          setCurrentMode(MODES.addAttachment);
        }}
      />
      <Icon
        src="stamp"
        className="icon-stamp blue"
        alt="Add stamp"
        showTooltip={true}
        height="25px"
        width="25px"
        onClick={() => setCurrentMode(MODES.addStamp)}
      />
      {/*<Icon
        src="ear"
        className="icon-otoscope blue"
        alt="Connect to Otoscope"
        showTooltip={true}
        height="25px"
        width="25px"
        onClick={() => {
          setCurrentMode(MODES.otoscope);
        }}
      />*/}
      <RemindersButton user={patient} width="25px" userRole="patient" />
      <>
        <Icon
          src="arrow-progress"
          fontAwesomeStyle="fas"
          alt="Flows"
          showTooltip={true}
          height="25px"
          width="25px"
          className="blue"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setFlowMenuAnchorEl(event.currentTarget);
          }}
        />
        {flowMenuAnchorEl && (
          <Menu
            anchorEl={flowMenuAnchorEl}
            keepMounted
            open={!!flowMenuAnchorEl}
            onClose={() => setFlowMenuAnchorEl(null)}
          >
            {Object.values(Flows).map(({ key, icon, label }) => (
              <MenuItem
                key={key}
                onClick={() => {
                  setFlowMenuAnchorEl(null);
                  setFlow(key);
                }}
              >
                <ListItemIcon>
                  <Icon src={icon} height="20px" />
                </ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
      <ImportCommentsFromDocuments
        key={documentsWithNotes.length}
        preSelected={documentsWithNotes}
        onSave={onInsertDocumentNotes}
        patient={patient}
      />
      {showBeautify && (
        <Icon
          src="microchip-ai"
          fontAwesomeStyle="fas"
          alt="Extract all Health Records"
          showTooltip={true}
          height="25px"
          width="25px"
          className="blue"
          onClick={() => showBeautify(true)}
        />
      )}
      <div className="double-icon cursor-pointer" onClick={() => setShowPSuggestion(true)}>
        <Icon
          src="p"
          alt="Complete 'P' section"
          showTooltip={true}
          height="25px"
          width="25px"
          className="blue"
        />
        <Icon
          src="sparkles"
          fontAwesomeStyle="fas"
          height="15px"
          width="15px"
          className="secondary blue"
        />
      </div>
      {showPSuggestion && (
        <PSuggestionModal
          patient={patient}
          onClose={() => setShowPSuggestion(false)}
          onSave={(text) => {
            insertText(text, true);
            setShowPSuggestion(false);
          }}
          note={note}
        />
      )}
      <hr />
      {sortedDocuments.map((documentId) => (
        <Thumbnail
          key={documentId}
          attachmentsCache={attachmentsCache}
          setCurrentMode={setCurrentMode}
          setCurrentAttachment={setCurrentAttachment}
          documentId={documentId}
        />
      ))}
    </div>
  );
};

const ImportCommentsFromDocuments = ({ patient, preSelected = DEFAULT_ARRAY, onSave }) => {
  const [modal, setModal] = useState(false);

  const {
    selected: checkedDocuments,
    toggleDocument,
    isDocumentSelected,
  } = useDocumentSelection({
    initialSelected: preSelected,
    mode: 'object',
  });

  const allDocuments = useAppSelector((state) => state.healthRecords.byId[patient.id]?.documents);

  const documentsWithNotes = useMemo(
    () =>
      _.sortBy(
        (allDocuments || []).filter(({ comments }) => comments),
        'date',
      ).reverse(),
    [allDocuments],
  );

  return (
    <>
      {modal && (
        <DefaultModal
          className="fit-modal document-notes-modal"
          title="Documents with Notes"
          wrap={true}
          padding={false}
          content={
            <>
              <List style={{ minWidth: '300px', minHeight: '300px' }}>
                {documentsWithNotes.length === 0 && (
                  <span className="ml-2">No documents to show</span>
                )}
                {documentsWithNotes.map((document, documentIndex) => (
                  <SelectableListItem
                    key={document.id}
                    selected={false}
                    onClick={() => {}}
                    item={
                      <DocumentBadge
                        item={document}
                        key={document.id}
                        firstLine={CalendarUtils.format(document.date)}
                        secondLine={document.comments}
                        className="long-second-line"
                      >
                        <DocumentCheck
                          document={document}
                          isChecked={isDocumentSelected(document)}
                          toggleDocument={toggleDocument}
                          documentIndex={documentIndex}
                        />
                      </DocumentBadge>
                    }
                  />
                ))}
              </List>
              <div className="button-wrapper">
                <DefaultButton
                  name="Insert Comments in Note"
                  className="blue"
                  style={{ width: '100%' }}
                  disabled={documentsWithNotes.length === 0}
                  onClick={() => {
                    onSave(checkedDocuments);
                    setModal(false);
                  }}
                />
              </div>
            </>
          }
          isOpen={true}
          onRequestClose={() => setModal(false)}
        />
      )}
      <Icon
        src="file-circle-check"
        fontAwesomeStyle="fas"
        alt="Import patient-facing notes from documents"
        showTooltip={true}
        height="25px"
        width="25px"
        className="blue"
        onClick={() => setModal(true)}
      >
        <UnreadBadge
          unreadCount={preSelected.length}
          style={{
            background: '#2693d1',
            position: 'absolute',
            right: '-6px',
            bottom: '-4px',
            pointerEvents: 'none',
          }}
        />
      </Icon>
    </>
  );
};

const PSuggestionModal = ({ patient, note, onSave, onClose }) => {
  const [text, setText] = useState('');
  const firstRun = useRef(true);

  const isLoading = useIsLoading(api.AI.runSavedPrompt);

  const patientAppointments = useAppSelector((state) => state.appointments.byId[patient.id]);
  const patientHealthRecords = useAppSelector((state) => state.healthRecords.byId[patient.id]);

  const patientReminders = useAppSelector((state) => state.reminders[patient.id]) || DEFAULT_ARRAY;

  const { runSavedPromptTyped, streamingText } = useAssistant('p-note-suggestion');

  useEffect(() => {
    if (firstRun.current === true) {
      const lastStartedAppointment = _.sortBy(patientAppointments || [], 'date')
        .reverse()
        .find(
          ({ status }) =>
            ![APPOINTMENT_STATUS.cancelled, APPOINTMENT_STATUS.no_show].includes(status),
        );

      let records = AIUtils.getRecordsForSmartP(
        lastStartedAppointment,
        patientHealthRecords,
        patientReminders,
      );

      firstRun.current = false;
      runSavedPromptTyped(
        'Note P Section Suggestions',
        { note, records },
        { patientId: patient.id },
      ).then((result) => {
        setText(result);
      });
    }
  }, [note, patientHealthRecords, patientReminders, patientAppointments, patient.id]);

  return (
    <DefaultModal
      className="note-p-modal fit-modal"
      wrap={true}
      padding={true}
      content={
        <>
          <ProgressMessage show={isLoading} />
          <div className="row">
            <TextareaAutosize
              label="Note"
              style={{ width: '600px', maxWidth: '90vw', overflow: 'auto' }}
              value={streamingText || text}
              disabled={isLoading}
              onChange={(event) => {
                let text = event.target.value;
                setText(text);
              }}
            />
          </div>
          <div className="row">
            <DefaultButton
              style={{ width: '100%' }}
              className="blue"
              onClick={() => onSave(text)}
              name="Add to Note"
              disabled={!text || isLoading}
            />
          </div>
        </>
      }
      isOpen={true}
      onRequestClose={onClose}
      title="Generate P Section"
    />
  );
};

export const Thumbnail = ({
  documentId,
  attachmentsCache,
  setCurrentMode,
  setCurrentAttachment,
}) => {
  const [url, setUrl] = useState();
  const [type, setType] = useState();
  const [document, setDocument] = useState();
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (attachmentsCache && attachmentsCache[documentId]) {
      //this is a cache so that we don't have to load the file as
      //soon as it gets uploaded
      setUrl(attachmentsCache[documentId]);
    } else {
      setLoading(true);
      dispatch(api.Documents.getDocumentFile({ documentId })).then((result) => {
        if (result) {
          setType(result.type);
          setUrl(MiscUtils.getBlobUrl(result));
        }
        setLoading(false);
      });
    }

    dispatch(api.Documents.getDocument({ documentId })).then((result) => {
      if (result) setDocument(result);
    });
  }, [dispatch, attachmentsCache, documentId]);

  let attachmentObject = { document, url, type };

  return (
    <div className="file-attachment-preview">
      {!!url && (
        <AttachmentPreview
          key={documentId}
          attachment={attachmentObject}
          onClick={() => {
            setCurrentMode(MODES.previewAttachment);
            setCurrentAttachment(attachmentObject);
          }}
        />
      )}
      <ProgressMessage show={loading} size={50} />
    </div>
  );
};

export const AttachmentPreview = ({ attachment = {}, onClick, showPdf = false }) => {
  let [openModal, setOpenModal] = useState(false);

  const type = attachment?.type;

  let description = (attachment.document || {}).title;

  return (
    <React.Fragment>
      {!!type && type.endsWith('application/pdf') && (
        <>
          {showPdf && (
            <DocumentPreview
              url={attachment.url}
              type={type}
              key={attachment.url}
              onClick={onClick ? onClick : () => setOpenModal(true)}
            />
          )}
          {!showPdf && (
            <Icon
              src="file-pdf"
              alt={description}
              showTooltip={!!description}
              height="50px"
              width="50px"
              onClick={onClick ? onClick : () => setOpenModal(true)}
            />
          )}
        </>
      )}
      {!!type && !type.endsWith('application/pdf') && (
        <img
          className="clickable"
          alt={description}
          src={attachment.url}
          onClick={onClick ? onClick : () => setOpenModal(true)}
        />
      )}
      {openModal && (
        <DefaultModal
          className="large-modal"
          key="preview-document-modal"
          content={
            <PreviewDocumentModal
              onClose={() => setOpenModal(false)}
              url={attachment.url}
              showFax={false}
            />
          }
          isOpen={openModal}
          onRequestClose={() => setOpenModal(false)}
        />
      )}
    </React.Fragment>
  );
};

function EditStampModal({ stamp, create, onRequestClose, onStampUpdated }) {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState(create ? {} : { ...stamp });
  const isEditing = useIsLoading(api.HealthRecords.addOrEditAppointmentNoteStamp);
  const isDeleting = useIsLoading(api.HealthRecords.deleteAppointmentNoteStamp);
  const isMakingDefault = useIsLoading(api.Physicians.updatePhysician);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const activePhysician = useAppSelector(GetPhysician);
  const defaultStampId = activePhysician?.preferences?.default_soap_stamp;
  const isDefaultStamp = !!defaultStampId && stamp.id === defaultStampId;

  function updateForm(ev) {
    setForm((prev) => ({
      ...prev,
      [ev.target.name]: ev.target.value,
    }));
  }

  function onClickSave() {
    dispatch(api.HealthRecords.addOrEditAppointmentNoteStamp({ appointmentNoteStamp: form }), {
      errorTitle: 'Edit Stamp',
    })
      .then((stamp) => stamp && onStampUpdated(form))
      .finally(onRequestClose);
  }

  async function onClickMakeDefault() {
    if (!activePhysician) {
      return;
    }
    /**@type {import('../../../../../generated/api').Physician} */
    const updated = {
      ...activePhysician,
      preferences: {
        ...(activePhysician.preferences ?? {}),
        default_soap_stamp: stamp.id,
      },
    };

    return dispatch(
      api.Physicians.updatePhysician({
        id: activePhysician.id,
        physician: updated,
      }),
    );
  }

  function onClickDelete() {
    dispatch(api.HealthRecords.deleteAppointmentNoteStamp({ id: stamp.id }), {
      errorTitle: 'Delete Stamp',
    }).finally(onRequestClose);
  }

  return (
    (!!stamp || create) && (
      <DefaultModal
        title="Edit Modal"
        isOpen={!!stamp || create}
        onRequestClose={onRequestClose}
        className="fit-modal"
        padding
        wrap
        draggable
        content={
          <div className="edit-stamp-modal-content flex flex-col">
            <DefaultTextField name="name" label="name" value={form.name} onChange={updateForm} />
            <span className="small-title mt-2">Content</span>
            <TextareaAutosize
              className="grow !overflow-scroll !max-w-none !max-h-screen !w-[600px] !h-[600px]"
              name="content"
              label="content"
              value={form.content}
              onChange={updateForm}
            />
            <div className="flex justify-evenly">
              <DefaultButton
                name="Delete"
                className="red"
                onClick={() => setShowConfirmation(true)}
                loading={isDeleting}
                disabled={isEditing}
              />
              <DefaultButton
                name={isDefaultStamp ? 'Already default' : 'Make Default'}
                onClick={onClickMakeDefault}
                loading={isMakingDefault}
                disabled={isDefaultStamp}
              />
              <DefaultButton
                name="Save"
                className="blue"
                onClick={onClickSave}
                loading={isEditing}
                disabled={isDeleting}
              />
            </div>
            {showConfirmation && (
              <DefaultModal
                title="Confirm Deletion"
                isOpen={showConfirmation}
                onRequestClose={() => setShowConfirmation(false)}
                className="fit-modal"
                wrap
                content={
                  <div className="stamp-confirm-deletion">
                    <span className="text-center">
                      Are you sure you want to delete the stamp <strong>{stamp.name}</strong>?
                      <br />
                      It will be deleted for everyone
                    </span>
                    <div className="mt-3 flex justify-evenly gap-2">
                      <DefaultButton
                        name="Cancel"
                        onClick={() => {
                          setShowConfirmation(false);
                        }}
                      />
                      <DefaultButton
                        icon="trash"
                        name="Delete"
                        className="red"
                        onClick={onClickDelete}
                      />
                    </div>
                  </div>
                }
              />
            )}
          </div>
        }
      />
    )
  );
}

export const StampPicker = ({
  currentStampId,
  onStampChanged,
  style = {},
  label = 'Stamps',
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const stampRefs = useAppSelector((state) => state.healthRecords.appointmentNoteStamps);
  const isNurse = useAppSelector(IsTypeNurse);
  const isLoadingStamps = useIsLoading(api.HealthRecords.getAppointmentNoteStamp);
  const filteredStamps = useMemo(() => {
    if (isNurse) {
      return stampRefs.filter((stamp) => stamp.name?.includes('RPN'));
    }
    return stampRefs;
  }, [stampRefs, isNurse]);

  return (
    <DefaultSelectField
      name="stamp"
      disabled={disabled}
      label={label}
      loading={isLoadingStamps}
      inputElements={filteredStamps.map(({ id, name }) => {
        return (
          <MenuItem value={id} key={id}>
            {name}
          </MenuItem>
        );
      })}
      onChange={(event) => {
        const id = event.target.value;
        onStampChanged(id);
      }}
      value={currentStampId || ''}
      style={{ marginTop: '0', ...style }}
    />
  );
};

/*
 *
  <Field
    name="stamp"
    label="Stamps"
    className="stamp-select flex-grow"
    inputs={filteredStamps.map(({ id, name }) => {
      return (
        <MenuItem value={id} key={id}>
          {name}
        </MenuItem>
      );
    })}
    onChange={(event) => {
      setCurrentStampId(event.target.value);
      onChange('stamp', event.target.value);
    }}
    value={currentStampId}
    component={UIUtils.getSelectField}
    style={{ width: '100%', marginTop: '0' }}
  />
 */
